import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import siteConfig from '../data/siteConfig.json';
import SafeIcon from '../common/SafeIcon';
import { FiMenu, FiX, FiPhone, FiGlobe } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const { lang, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.services'), path: '/services' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.gallery'), path: '/gallery' },
    { name: t('nav.reviews'), path: '/reviews' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-3' : 'bg-glass-dark/95 text-white py-5'
      }`}
    >
      <div className="container mx-auto px-6 max-w-7xl flex justify-between items-center">
        
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          {siteConfig.brand.logoUrl ? (
            <img
              src={siteConfig.brand.logoUrl}
              alt={siteConfig.brand.logoAlt}
              className="w-10 h-10 rounded-lg object-cover transform group-hover:scale-105 transition-transform"
            />
          ) : (
            <div className="w-10 h-10 bg-glass-blue rounded-lg flex items-center justify-center transform group-hover:scale-105 transition-transform">
              <SafeIcon icon={FiGlobe} className="text-white text-xl" />
            </div>
          )}
          <div>
            <h1 className={`font-bold text-xl leading-tight ${isScrolled ? 'text-glass-dark' : 'text-white'}`}>
              {siteConfig.business.name.replace(' Elite', '')} <span className="text-glass-blue">Elite</span>
            </h1>
            <p className={`text-[10px] uppercase tracking-wider ${isScrolled ? 'text-slate-500' : 'text-slate-300'}`}>
              {siteConfig.business.tagline}
            </p>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link 
              key={link.path} 
              to={link.path}
              className={`font-medium text-sm hover:text-glass-blue transition-colors ${
                isScrolled ? 'text-slate-700' : 'text-slate-200'
              } ${location.pathname === link.path ? '!text-glass-blue' : ''}`}
            >
              {link.name}
            </Link>
          ))}
        </nav>

        {/* Actions */}
        <div className="hidden lg:flex items-center gap-6">
          <button 
            onClick={toggleLanguage}
            className={`font-bold text-sm tracking-wider flex items-center gap-1 hover:text-glass-blue transition-colors ${
              isScrolled ? 'text-slate-700' : 'text-slate-200'
            }`}
          >
            {lang === 'en' ? 'FR' : 'EN'}
          </button>
          
          <a href={`tel:${siteConfig.business.phoneHref}`} className="bg-glass-blue hover:bg-blue-700 text-white px-6 py-2.5 rounded-full font-semibold text-sm flex items-center gap-2 transition-all shadow-lg shadow-glass-blue/20 hover:shadow-glass-blue/40 transform hover:-translate-y-0.5">
            <SafeIcon icon={FiPhone} />
            {t('common.callNow')}
          </a>
        </div>

        {/* Mobile Toggle */}
        <button 
          className="lg:hidden text-2xl"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className={isScrolled ? 'text-glass-dark' : 'text-white'} />
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden absolute top-full left-0 right-0 bg-white border-b border-slate-100 shadow-xl overflow-hidden"
          >
            <div className="flex flex-col p-6 gap-4">
              {navLinks.map((link) => (
                <Link 
                  key={link.path} 
                  to={link.path}
                  className="text-slate-800 font-medium text-lg border-b border-slate-100 pb-3"
                >
                  {link.name}
                </Link>
              ))}
              <div className="flex items-center justify-between mt-4">
                <button 
                  onClick={toggleLanguage}
                  className="font-bold text-slate-800 bg-slate-100 px-4 py-2 rounded-lg"
                >
                  {lang === 'en' ? 'Passer en Français' : 'Switch to English'}
                </button>
                <a href={`tel:${siteConfig.business.phoneHref}`} className="bg-glass-blue text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2">
                  <SafeIcon icon={FiPhone} />
                  {t('common.callNow')}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
